import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Hls from 'hls.js'
import { api } from '@/lib/api'
import { Plus, Search, Edit, Trash2, X, Play, Wifi, WifiOff, Square, RefreshCw, Monitor, Terminal, Repeat, Zap, ChevronUp, ChevronDown, Tv, Check } from 'lucide-react'

interface StreamSource {
  url: string
  priority: number
}

interface Stream {
  id: string
  name: string
  type: 'live' | 'movie' | 'series'
  category_id?: string
  sources: StreamSource[]
  icon_url?: string
  epg_channel_id?: string
  custom_user_agent?: string
  notes?: string
  channel_number?: number
  is_active: boolean
  fix_timestamps: boolean
  startup_mode: 'on_demand' | 'always_on'
  viewer_count: number
  is_online: boolean
  // Health/state info
  state?: 'idle' | 'starting' | 'running' | 'failover' | 'stopping' | 'error'
  error_message?: string
  source_healthy?: boolean
  current_source?: number
  // Technical stream info (only available when stream is running)
  video_codec?: string
  audio_codec?: string
  resolution?: string
  fps?: string
  bitrate?: string
  started_at?: string
  created_at: string
}

interface EPGChannel {
  channel_id: string
  source_id: string
  name: string
  display_name: string
  icon?: string
  source_name: string
}

// Helper to create composite EPG key (source_id:channel_id)
const makeEpgKey = (channel: EPGChannel) => `${channel.source_id}:${channel.channel_id}`

interface TestStreamData {
  status: string
  stream_id: string
  stream_name: string
  state: string
  logs: string[]
  tech_info?: {
    video_codec?: string
    audio_codec?: string
    resolution?: string
    fps?: string
    bitrate?: string
  }
  error?: string
}

interface StreamFormData {
  name: string
  type: 'live' | 'movie' | 'series'
  category_id: string
  sources: StreamSource[]
  icon_url: string
  epg_channel_id: string
  custom_user_agent: string
  notes: string
  channel_number: string  // Keep as string for form input, convert to number on submit
  is_active: boolean
  fix_timestamps: boolean
  startup_mode: 'on_demand' | 'always_on'
}

interface Category {
  id: string
  name: string
  type: string
  channel_range_start?: number
  channel_range_end?: number
  auto_assign_numbers?: boolean
}

const defaultFormData: StreamFormData = {
  name: '',
  type: 'live',
  category_id: '',
  sources: [{ url: '', priority: 1 }],
  icon_url: '',
  epg_channel_id: '',
  custom_user_agent: '',
  notes: '',
  channel_number: '',
  is_active: true,
  fix_timestamps: false,
  startup_mode: 'on_demand',
}

export default function Streams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  // Initialize category filter from URL params (supports "none" for unassigned)
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') || '')
  const [statusFilter, setStatusFilter] = useState('') // '', 'active', 'inactive', 'online', 'offline'
  const [epgFilter, setEpgFilter] = useState('') // '', 'has_epg', 'no_epg'
  const [page, setPage] = useState(1)

  // Bulk selection state
  const [selectedStreams, setSelectedStreams] = useState<Set<string>>(new Set())
  const [bulkCategoryModalOpen, setBulkCategoryModalOpen] = useState(false)
  const [bulkUserAgentModalOpen, setBulkUserAgentModalOpen] = useState(false)
  const [bulkUserAgent, setBulkUserAgent] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingStream, setEditingStream] = useState<Stream | null>(null)
  const [formData, setFormData] = useState<StreamFormData>(defaultFormData)
  const [error, setError] = useState('')
  const [epgSearch, setEpgSearch] = useState('')

  // Update URL when category filter changes
  useEffect(() => {
    if (categoryFilter) {
      setSearchParams({ category: categoryFilter }, { replace: true })
    } else {
      // Remove category param if no filter
      const params = new URLSearchParams(searchParams)
      params.delete('category')
      setSearchParams(params, { replace: true })
    }
  }, [categoryFilter, setSearchParams])

  // Handle edit param from URL (e.g., from Dashboard click)
  const editStreamId = searchParams.get('edit')
  useEffect(() => {
    if (editStreamId) {
      // Fetch the stream and open edit modal
      api.getStream(editStreamId).then((stream: Stream) => {
        // Ensure we have at least one source slot
        const sources = stream.sources?.length > 0
          ? stream.sources.map((s, i) => ({ url: s.url, priority: i + 1 }))
          : [{ url: '', priority: 1 }]
        setEditingStream(stream)
        setFormData({
          name: stream.name,
          type: stream.type,
          category_id: stream.category_id || '',
          sources,
          icon_url: stream.icon_url || '',
          epg_channel_id: stream.epg_channel_id || '',
          custom_user_agent: stream.custom_user_agent || '',
          notes: stream.notes || '',
          channel_number: stream.channel_number?.toString() || '',
          is_active: stream.is_active,
          fix_timestamps: stream.fix_timestamps || false,
          startup_mode: stream.startup_mode || 'on_demand',
        })
        setError('')
        setModalOpen(true)
        // Clear the edit param from URL
        const params = new URLSearchParams(searchParams)
        params.delete('edit')
        setSearchParams(params, { replace: true })
      }).catch(() => {
        // Stream not found, just clear the param
        const params = new URLSearchParams(searchParams)
        params.delete('edit')
        setSearchParams(params, { replace: true })
      })
    }
  }, [editStreamId])

  // Test stream modal state
  const [testModalOpen, setTestModalOpen] = useState(false)
  const [testingStream, setTestingStream] = useState<Stream | null>(null)
  const [testStreamData, setTestStreamData] = useState<TestStreamData | null>(null)
  const [testLoading, setTestLoading] = useState(false)
  const logContainerRef = useRef<HTMLDivElement>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)

  const queryClient = useQueryClient()

  // Build query params
  const queryParams: {
    page: number
    per_page: number
    search?: string
    type?: string
    category_id?: string
    is_active?: string
    is_online?: string
    has_epg?: string
  } = { page, per_page: 20 }
  if (search) queryParams.search = search
  if (typeFilter) queryParams.type = typeFilter
  if (categoryFilter) queryParams.category_id = categoryFilter
  // Server-side filters for status
  if (statusFilter === 'disabled') queryParams.is_active = 'false'
  if (statusFilter === 'live') queryParams.is_online = 'true'
  // EPG filter
  if (epgFilter === 'has_epg') queryParams.has_epg = 'true'
  if (epgFilter === 'no_epg') queryParams.has_epg = 'false'

  const { data, isLoading } = useQuery({
    queryKey: ['streams', page, search, typeFilter, categoryFilter, statusFilter, epgFilter],
    queryFn: () => api.getStreams(queryParams),
    refetchInterval: 5000, // Auto-refresh every 5 seconds for live viewer counts
  })

  // Fetch EPG channels for dropdown
  const { data: epgChannelsData } = useQuery({
    queryKey: ['epg-channels'],
    queryFn: () => api.getEPGChannels(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
  const epgChannels: EPGChannel[] = epgChannelsData?.channels || []

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.getCategories({}),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
  const categories: Category[] = categoriesData || []

  // Filter EPG channels by search term
  const filteredEpgChannels = epgChannels.filter(channel =>
    epgSearch === '' ||
    channel.name?.toLowerCase().includes(epgSearch.toLowerCase()) ||
    channel.channel_id?.toLowerCase().includes(epgSearch.toLowerCase()) ||
    channel.source_name?.toLowerCase().includes(epgSearch.toLowerCase())
  ).slice(0, 100) // Limit to 100 results for performance

  // Group filtered EPG channels by source for better organization
  const epgChannelsBySource = filteredEpgChannels.reduce((acc, channel) => {
    const source = channel.source_name || 'Unknown Source'
    if (!acc[source]) {
      acc[source] = []
    }
    acc[source].push(channel)
    return acc
  }, {} as Record<string, EPGChannel[]>)

  const createMutation = useMutation({
    mutationFn: (data: StreamFormData) => api.createStream({
      name: data.name,
      type: data.type,
      category_id: data.category_id || undefined,
      sources: data.sources.filter(s => s.url.trim() !== ''),
      icon_url: data.icon_url || undefined,
      epg_channel_id: data.epg_channel_id || undefined,
      custom_user_agent: data.custom_user_agent || undefined,
      notes: data.notes || undefined,
      channel_number: data.channel_number ? parseInt(data.channel_number, 10) : undefined,
      is_active: data.is_active,
      fix_timestamps: data.fix_timestamps,
      startup_mode: data.startup_mode,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['streams'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create stream')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: StreamFormData }) =>
      api.updateStream(id, {
        name: data.name,
        category_id: data.category_id || undefined,
        sources: data.sources.filter(s => s.url.trim() !== ''),
        icon_url: data.icon_url || undefined,
        epg_channel_id: data.epg_channel_id || undefined,
        custom_user_agent: data.custom_user_agent || undefined,
        notes: data.notes || undefined,
        channel_number: data.channel_number ? parseInt(data.channel_number, 10) : undefined,
        is_active: data.is_active,
        fix_timestamps: data.fix_timestamps,
        startup_mode: data.startup_mode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['streams'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update stream')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteStream(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['streams'] })
    },
  })

  // Bulk update mutation
  const batchUpdateMutation = useMutation({
    mutationFn: ({ streamIds, updates }: {
      streamIds: string[]
      updates: { is_active?: boolean; category_id?: string | null; custom_user_agent?: string }
    }) => api.batchUpdateStreams(streamIds, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['streams'] })
      setSelectedStreams(new Set())
      setBulkCategoryModalOpen(false)
      setBulkUserAgentModalOpen(false)
      setBulkUserAgent('')
    },
  })

  const openCreateModal = () => {
    setEditingStream(null)
    setFormData(defaultFormData)
    setError('')
    setModalOpen(true)
  }

  const openEditModal = (stream: Stream) => {
    setEditingStream(stream)
    // Ensure we have at least one source slot
    const sources = stream.sources?.length > 0
      ? stream.sources.map((s, i) => ({ url: s.url, priority: i + 1 }))
      : [{ url: '', priority: 1 }]
    setFormData({
      name: stream.name,
      type: stream.type,
      category_id: stream.category_id || '',
      sources,
      icon_url: stream.icon_url || '',
      epg_channel_id: stream.epg_channel_id || '',
      custom_user_agent: stream.custom_user_agent || '',
      notes: stream.notes || '',
      channel_number: stream.channel_number?.toString() || '',
      is_active: stream.is_active,
      fix_timestamps: stream.fix_timestamps || false,
      startup_mode: stream.startup_mode || 'on_demand',
    })
    setError('')
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingStream(null)
    setFormData(defaultFormData)
    setError('')
    setEpgSearch('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Check at least one source has a URL
    const validSources = formData.sources.filter(s => s.url.trim() !== '')
    if (validSources.length === 0) {
      setError('At least one source URL is required')
      return
    }

    if (editingStream) {
      updateMutation.mutate({ id: editingStream.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (stream: Stream) => {
    if (confirm(`Delete stream "${stream.name}"?`)) {
      deleteMutation.mutate(stream.id)
    }
  }

  // Test stream functions
  const openTestModal = async (stream: Stream) => {
    setTestingStream(stream)
    setTestStreamData(null)
    setTestLoading(true)
    setTestModalOpen(true)

    try {
      const response = await api.testStream(stream.id)
      setTestStreamData(response)
      // Start polling for logs
      startLogPolling(stream.id)
    } catch (err: any) {
      setTestStreamData({
        status: 'error',
        stream_id: stream.id,
        stream_name: stream.name,
        state: 'error',
        logs: [],
        error: err.response?.data?.error || 'Failed to start test stream'
      })
    } finally {
      setTestLoading(false)
    }
  }

  const startLogPolling = (streamId: string) => {
    // Clear any existing poll
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    // Poll logs every 2 seconds
    pollIntervalRef.current = window.setInterval(async () => {
      try {
        const response = await api.getStreamLogs(streamId)
        setTestStreamData(prev => prev ? { ...prev, logs: response.logs || [], state: response.state } : prev)
        // Auto-scroll to bottom
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
        }
      } catch {
        // Ignore errors during polling
      }
    }, 2000)
  }

  const stopLogPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const closeTestModal = async () => {
    stopLogPolling()
    // Cleanup HLS
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }
    // Stop the test stream if it has data (stream was started)
    if (testingStream && testStreamData && !testStreamData.error) {
      try {
        await api.stopTestStream(testingStream.id)
      } catch {
        // Ignore errors when stopping
      }
    }
    setTestModalOpen(false)
    setTestingStream(null)
    setTestStreamData(null)
  }

  const refreshLogs = async () => {
    if (!testingStream) return
    try {
      const response = await api.getStreamLogs(testingStream.id)
      setTestStreamData(prev => prev ? { ...prev, logs: response.logs || [], state: response.state } : prev)
    } catch {
      // Ignore errors
    }
  }

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopLogPolling()
      if (hlsRef.current) {
        hlsRef.current.destroy()
      }
    }
  }, [])

  // Setup HLS when stream is running and we have logs indicating segments are ready
  useEffect(() => {
    if (!testingStream || !testStreamData || testStreamData.error) return
    if (!videoRef.current) return

    // Check if we have at least a few segments written (look for segment_2 or higher in logs)
    const hasSegments = testStreamData.logs?.some(log =>
      log.includes('segment_2.ts') || log.includes('segment_3.ts') || log.includes('segment_4.ts')
    )

    if (!hasSegments) return
    if (hlsRef.current) return // Already setup

    const hlsUrl = `/hls/${testingStream.id}/playlist.m3u8`

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
      })
      hls.loadSource(hlsUrl)
      hls.attachMedia(videoRef.current)
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoRef.current?.play().catch(() => {})
      })
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data)
        }
      })
      hlsRef.current = hls
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      videoRef.current.src = hlsUrl
      videoRef.current.play().catch(() => {})
    }
  }, [testingStream, testStreamData])

  const rawStreams = data?.streams || []
  const total = data?.total || 0
  const totalPages = data?.total_pages || 1

  // Apply client-side filtering for runtime status (not in DB)
  // Note: 'live' and 'disabled' are now filtered server-side
  const streams = rawStreams.filter((stream: Stream) => {
    if (statusFilter === 'ready') return !stream.is_online && stream.is_active && stream.state !== 'error' && !stream.error_message
    if (statusFilter === 'error') return stream.state === 'error' || (stream.error_message && stream.error_message.length > 0)
    return true
  })

  // Selection helpers
  const toggleStreamSelection = (streamId: string) => {
    setSelectedStreams(prev => {
      const newSet = new Set(prev)
      if (newSet.has(streamId)) {
        newSet.delete(streamId)
      } else {
        newSet.add(streamId)
      }
      return newSet
    })
  }

  const toggleSelectAllOnPage = () => {
    const currentPageIds = streams.map((s: Stream) => s.id)
    const allSelected = currentPageIds.every((id: string) => selectedStreams.has(id))

    if (allSelected) {
      // Deselect all on this page
      setSelectedStreams(prev => {
        const newSet = new Set(prev)
        currentPageIds.forEach((id: string) => newSet.delete(id))
        return newSet
      })
    } else {
      // Select all on this page
      setSelectedStreams(prev => {
        const newSet = new Set(prev)
        currentPageIds.forEach((id: string) => newSet.add(id))
        return newSet
      })
    }
  }

  const clearSelection = () => {
    setSelectedStreams(new Set())
  }

  // Check if all streams on current page are selected
  const allOnPageSelected = streams.length > 0 && streams.every((s: Stream) => selectedStreams.has(s.id))

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Streams</h1>
        <button onClick={openCreateModal} className="btn-primary shrink-0">
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Stream</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search streams..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="input-field pl-12"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
            className="input-field w-auto"
          >
            <option value="">All Types</option>
            <option value="live">Live</option>
            <option value="movie">Movie</option>
            <option value="series">Series</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1) }}
            className="input-field w-auto"
          >
            <option value="">All Categories</option>
            <option value="none">Unassigned</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="input-field w-auto"
          >
            <option value="">All Status</option>
            <option value="live">Live</option>
            <option value="ready">Ready</option>
            <option value="error">Error</option>
            <option value="disabled">Disabled</option>
          </select>
          <select
            value={epgFilter}
            onChange={(e) => { setEpgFilter(e.target.value); setPage(1) }}
            className="input-field w-auto"
          >
            <option value="">All EPG</option>
            <option value="has_epg">Has EPG</option>
            <option value="no_epg">No EPG</option>
          </select>
          {(search || typeFilter || categoryFilter || statusFilter || epgFilter) && (
            <button
              onClick={() => {
                setSearch('')
                setTypeFilter('')
                setCategoryFilter('')
                setStatusFilter('')
                setEpgFilter('')
                setPage(1)
              }}
              className="btn-secondary whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Pagination Bar - Top */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-card rounded-lg border border-border px-4 py-3">
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{total}</span> streams
            {totalPages > 1 && (
              <span> &middot; Page <span className="font-medium text-foreground">{page}</span> of <span className="font-medium text-foreground">{totalPages}</span></span>
            )}
          </p>
          {(search || typeFilter || categoryFilter || statusFilter || epgFilter) && (
            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
              Filtered
            </span>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="btn-secondary px-3 py-1.5 text-sm"
              title="First page"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary px-3 py-1.5 text-sm"
            >
              Prev
            </button>
            <span className="text-sm text-muted-foreground px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary px-3 py-1.5 text-sm"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="btn-secondary px-3 py-1.5 text-sm"
              title="Last page"
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedStreams.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-primary/10 border border-primary/30 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-primary">
            {selectedStreams.size} selected
          </span>
          <div className="h-4 w-px bg-primary/30" />
          <button
            onClick={() => batchUpdateMutation.mutate({
              streamIds: Array.from(selectedStreams),
              updates: { is_active: true }
            })}
            disabled={batchUpdateMutation.isPending}
            className="btn-secondary text-sm px-3 py-1.5"
          >
            Enable
          </button>
          <button
            onClick={() => batchUpdateMutation.mutate({
              streamIds: Array.from(selectedStreams),
              updates: { is_active: false }
            })}
            disabled={batchUpdateMutation.isPending}
            className="btn-secondary text-sm px-3 py-1.5"
          >
            Disable
          </button>
          <div className="h-4 w-px bg-primary/30" />
          <button
            onClick={() => setBulkCategoryModalOpen(true)}
            disabled={batchUpdateMutation.isPending}
            className="btn-secondary text-sm px-3 py-1.5"
          >
            Change Category
          </button>
          <button
            onClick={() => setBulkUserAgentModalOpen(true)}
            disabled={batchUpdateMutation.isPending}
            className="btn-secondary text-sm px-3 py-1.5"
          >
            Set User-Agent
          </button>
          <div className="h-4 w-px bg-primary/30" />
          <button
            onClick={clearSelection}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear Selection
          </button>
          {batchUpdateMutation.isPending && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary ml-2"></div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-4 text-left">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleSelectAllOnPage}
                  className="w-4 h-4 rounded border-border"
                  title={allOnPageSelected ? "Deselect all on page" : "Select all on page"}
                />
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Name</th>
              <th className="px-3 py-4 text-center text-sm font-medium text-muted-foreground" title="EPG Guide">
                <Tv className="w-4 h-4 mx-auto" />
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Type</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Source</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Viewers</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-muted-foreground">Actions</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Stream Info</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : streams.length > 0 ? (
              streams.map((stream: Stream) => {
                // Calculate status display with health indicators
                const getStatusDisplay = (): { label: string; className: string; icon?: string; tooltip?: string } => {
                  if (!stream.is_active) {
                    return { label: 'Disabled', className: 'badge-destructive', icon: '⏸', tooltip: 'Stream is disabled' }
                  }

                  // Handle error state
                  if (stream.state === 'error' || (stream.error_message && stream.error_message.length > 0)) {
                    return {
                      label: 'Error',
                      className: 'badge-destructive',
                      icon: '❌',
                      tooltip: stream.error_message || 'Stream encountered an error'
                    }
                  }

                  // Handle failover state
                  if (stream.state === 'failover') {
                    return {
                      label: 'Failover',
                      className: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
                      icon: '⚠️',
                      tooltip: `Switching to backup source ${(stream.current_source || 0) + 1}`
                    }
                  }

                  // Handle starting state
                  if (stream.state === 'starting') {
                    return { label: 'Starting', className: 'badge-secondary', icon: '🔄', tooltip: 'Stream is starting up' }
                  }

                  // Handle stopping state
                  if (stream.state === 'stopping') {
                    return { label: 'Stopping', className: 'badge-secondary', icon: '⏹', tooltip: 'Stream is stopping' }
                  }

                  // Running with health info
                  if (stream.is_online && stream.started_at) {
                    const startedAt = new Date(stream.started_at)
                    const now = new Date()
                    const diffMs = now.getTime() - startedAt.getTime()
                    const diffMins = Math.floor(diffMs / 60000)
                    const diffHours = Math.floor(diffMins / 60)
                    const diffDays = Math.floor(diffHours / 24)

                    let duration: string
                    if (diffDays > 0) {
                      duration = `${diffDays}d ${diffHours % 24}h`
                    } else if (diffHours > 0) {
                      duration = `${diffHours}h ${diffMins % 60}m`
                    } else {
                      duration = `${diffMins}m`
                    }

                    // Check source health
                    if (stream.source_healthy === false) {
                      return {
                        label: `Degraded ${duration}`,
                        className: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
                        icon: '⚠️',
                        tooltip: 'Source may be experiencing issues'
                      }
                    }

                    return {
                      label: `Live ${duration}`,
                      className: 'badge-success',
                      icon: '🟢',
                      tooltip: `Streaming for ${duration}${stream.viewer_count > 0 ? ` • ${stream.viewer_count} viewer${stream.viewer_count > 1 ? 's' : ''}` : ''}`
                    }
                  }

                  if (stream.is_online) {
                    return { label: 'Live', className: 'badge-success', icon: '🟢', tooltip: 'Stream is live' }
                  }

                  // Idle/On-demand
                  if (stream.state === 'idle') {
                    return { label: 'Idle', className: 'badge-secondary', icon: '🟡', tooltip: 'Ready, waiting for viewers' }
                  }

                  return { label: 'Ready', className: 'badge-secondary', icon: '⚪', tooltip: 'On-demand: starts when a viewer connects' }
                }
                const status = getStatusDisplay()

                return (
                <tr key={stream.id} className={`hover:bg-muted/30 transition-colors ${selectedStreams.has(stream.id) ? 'bg-primary/5' : ''}`}>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedStreams.has(stream.id)}
                      onChange={() => toggleStreamSelection(stream.id)}
                      className="w-4 h-4 rounded border-border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-base font-medium text-foreground">{stream.name}</span>
                  </td>
                  <td className="px-3 py-4 text-center">
                    {stream.epg_channel_id ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10" title={`EPG: ${stream.epg_channel_id}`}>
                        <Tv className="w-3.5 h-3.5 text-primary" />
                      </span>
                    ) : (
                      <span className="text-muted-foreground/30">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="badge-primary capitalize">{stream.type}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground truncate block max-w-xs">
                        {stream.sources?.[0]?.url || 'No source'}
                      </span>
                      {stream.sources && stream.sources.length > 1 && (
                        <span className="text-xs text-primary">
                          {stream.is_online && stream.current_source !== undefined
                            ? `Source ${stream.current_source + 1}/${stream.sources.length} active`
                            : `${stream.sources.length} sources configured`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {stream.is_online ? (
                        <Wifi className="w-4 h-4 text-success" />
                      ) : (
                        <WifiOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className={`text-base ${stream.viewer_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {stream.viewer_count}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`${status.className} cursor-help`}
                      title={status.tooltip}
                    >
                      {status.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      {stream.is_online ? (
                        <>
                          {/* Failover button - only show if stream has multiple sources */}
                          {stream.sources && stream.sources.length > 1 && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.forceFailover(stream.id)
                                  queryClient.invalidateQueries({ queryKey: ['streams'] })
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Failover failed')
                                }
                              }}
                              className="p-2 rounded-lg text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
                              title={`Force failover (${stream.sources.length} sources, currently using #${(stream.current_source || 0) + 1})`}
                            >
                              <Repeat className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              try {
                                await api.stopTestStream(stream.id)
                                queryClient.invalidateQueries({ queryKey: ['streams'] })
                              } catch {}
                            }}
                            className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Stop stream"
                          >
                            <Square className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Probe button */}
                          <button
                            onClick={async () => {
                              try {
                                const result = await api.probeStream(stream.id)
                                if (result.success && result.info) {
                                  const info = result.info
                                  const video = info.video_info
                                  const audio = info.audio_info
                                  let msg = `Stream Info:\n`
                                  msg += `Format: ${info.format || 'unknown'}\n`
                                  msg += `Live: ${info.is_live ? 'Yes' : 'No'}\n`
                                  if (video) {
                                    msg += `\nVideo:\n`
                                    msg += `  Codec: ${video.codec || 'unknown'}\n`
                                    msg += `  Resolution: ${video.width}x${video.height}\n`
                                    msg += `  FPS: ${video.framerate?.toFixed(2) || 'unknown'}\n`
                                  }
                                  if (audio) {
                                    msg += `\nAudio:\n`
                                    msg += `  Codec: ${audio.codec || 'unknown'}\n`
                                    msg += `  Channels: ${audio.channels || 'unknown'}\n`
                                    msg += `  Sample Rate: ${audio.sample_rate || 'unknown'} Hz\n`
                                  }
                                  msg += `\nProbe Time: ${(info.probe_time / 1000000).toFixed(0)}ms`
                                  alert(msg)
                                } else {
                                  alert(`Probe failed: ${result.error || 'Unknown error'}`)
                                }
                              } catch (err: any) {
                                alert(err.response?.data?.error || 'Probe failed')
                              }
                            }}
                            className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                            title="Probe stream source"
                          >
                            <Zap className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openTestModal(stream)}
                            className="p-2 rounded-lg text-muted-foreground hover:text-success hover:bg-success/10 transition-colors"
                            title="Test stream"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openEditModal(stream)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        title="Edit stream"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(stream)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        title="Delete stream"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {stream.is_online && (stream.resolution || stream.video_codec || stream.audio_codec) ? (
                      <div className="flex flex-col gap-1">
                        {stream.resolution && (
                          <span className="text-sm font-medium text-foreground">{stream.resolution}</span>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {stream.video_codec && <span className="badge-secondary text-xs">{stream.video_codec}</span>}
                          {stream.audio_codec && <span className="badge-secondary text-xs">{stream.audio_codec}</span>}
                          {stream.fps && <span>{stream.fps} fps</span>}
                        </div>
                        {stream.bitrate && (
                          <span className="text-xs text-muted-foreground">{stream.bitrate}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              )})
            ) : (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-base text-muted-foreground">
                  No streams found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-card rounded-lg border border-border w-full max-w-lg max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                {editingStream ? 'Edit Stream' : 'Add Stream'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {error && (
                <div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-base">
                  {error}
                </div>
              )}

            <form id="stream-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="form-label">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="input-field"
                >
                  <option value="live">Live</option>
                  <option value="movie">Movie</option>
                  <option value="series">Series</option>
                </select>
              </div>

              <div>
                <label className="form-label">Category (optional)</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">No category</option>
                  {categories
                    .filter(cat => !formData.type || cat.type === formData.type)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Multiple Sources */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="form-label mb-0">Sources (up to 5)</label>
                  {formData.sources.length < 5 && (
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        sources: [...formData.sources, { url: '', priority: formData.sources.length + 1 }]
                      })}
                      className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Add Source
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {formData.sources.map((source, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground w-6">#{index + 1}</span>
                      <input
                        type="text"
                        value={source.url}
                        onChange={(e) => {
                          const newSources = [...formData.sources]
                          newSources[index] = { ...newSources[index], url: e.target.value }
                          setFormData({ ...formData, sources: newSources })
                        }}
                        className="input-field flex-1"
                        placeholder={index === 0 ? "Primary source URL (required)" : "Backup source URL (optional)"}
                      />
                      {/* Reorder buttons */}
                      {formData.sources.length > 1 && (
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => {
                              if (index === 0) return
                              const newSources = [...formData.sources]
                              ;[newSources[index - 1], newSources[index]] = [newSources[index], newSources[index - 1]]
                              newSources.forEach((s, i) => s.priority = i + 1)
                              setFormData({ ...formData, sources: newSources })
                            }}
                            disabled={index === 0}
                            className={`p-0.5 rounded ${index === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
                            title="Move up"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (index === formData.sources.length - 1) return
                              const newSources = [...formData.sources]
                              ;[newSources[index], newSources[index + 1]] = [newSources[index + 1], newSources[index]]
                              newSources.forEach((s, i) => s.priority = i + 1)
                              setFormData({ ...formData, sources: newSources })
                            }}
                            disabled={index === formData.sources.length - 1}
                            className={`p-0.5 rounded ${index === formData.sources.length - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
                            title="Move down"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {formData.sources.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSources = formData.sources.filter((_, i) => i !== index)
                              .map((s, i) => ({ ...s, priority: i + 1 }))
                            setFormData({ ...formData, sources: newSources })
                          }}
                          className="p-2 text-muted-foreground hover:text-destructive"
                          title="Remove source"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.sources.length > 1
                    ? `${formData.sources.length} sources configured. Auto-failover if primary fails. Use arrows to reorder.`
                    : 'Add backup sources for automatic failover.'}
                </p>
              </div>

              <div>
                <label className="form-label">Icon URL (optional)</label>
                <input
                  type="text"
                  value={formData.icon_url}
                  onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                  className="input-field"
                  placeholder="http://example.com/icon.png"
                />
              </div>

              {/* EPG Channel Selection */}
              {formData.type === 'live' && (
                <div>
                  <label className="form-label">EPG Channel (optional)</label>

                  {/* Current Selection Display */}
                  {formData.epg_channel_id && (
                    <div className="mb-3 p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Tv className="w-4 h-4 text-primary" />
                        <div>
                          <span className="text-sm font-medium text-foreground">
                            {(() => {
                              const [sourceId, channelId] = formData.epg_channel_id.split(':')
                              const channel = epgChannels.find(c => c.source_id === sourceId && c.channel_id === channelId)
                              return channel?.display_name || channel?.name || formData.epg_channel_id
                            })()}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {(() => {
                              const [sourceId] = formData.epg_channel_id.split(':')
                              const channel = epgChannels.find(c => c.source_id === sourceId)
                              return channel?.source_name || ''
                            })()}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, epg_channel_id: '' })}
                        className="p-1 text-muted-foreground hover:text-destructive rounded"
                        title="Remove EPG"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={epgSearch}
                      onChange={(e) => setEpgSearch(e.target.value)}
                      placeholder={formData.epg_channel_id ? "Search to change EPG..." : "Search EPG channels..."}
                      className="input-field pl-10"
                    />
                  </div>

                  {/* Results List - only show when searching or no EPG assigned */}
                  {(epgSearch || !formData.epg_channel_id) && (
                    <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded-lg bg-background">
                      {!formData.epg_channel_id && !epgSearch && (
                        <div className="px-3 py-2 text-sm text-muted-foreground italic">
                          Type to search {epgChannels.length} EPG channels...
                        </div>
                      )}
                      {epgSearch && filteredEpgChannels.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No channels matching "{epgSearch}"
                        </div>
                      )}
                      {Object.entries(epgChannelsBySource).map(([sourceName, channels]) => (
                        <div key={sourceName}>
                          <div className="px-3 py-1.5 bg-muted/50 text-xs font-medium text-muted-foreground sticky top-0">
                            {sourceName}
                          </div>
                          {channels.map((channel) => {
                            const key = makeEpgKey(channel)
                            const isSelected = formData.epg_channel_id === key
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, epg_channel_id: key })
                                  setEpgSearch('')
                                }}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/10' : ''}`}
                              >
                                <span className={isSelected ? 'font-medium text-primary' : 'text-foreground'}>
                                  {channel.display_name || channel.name || channel.channel_id}
                                </span>
                                {isSelected && <Check className="w-4 h-4 text-primary" />}
                              </button>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground mt-2">
                    {epgChannels.length} channels available
                    {epgSearch && ` • ${filteredEpgChannels.length} matching`}
                  </p>
                </div>
              )}

              {/* Custom User-Agent */}
              <div>
                <label className="form-label">Custom User-Agent (optional)</label>
                <input
                  type="text"
                  value={formData.custom_user_agent}
                  onChange={(e) => setFormData({ ...formData, custom_user_agent: e.target.value })}
                  className="input-field"
                  placeholder="Leave empty to use global default"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Override the global User-Agent for this stream
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="form-label">Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value.slice(0, 200) })}
                  className="input-field resize-none"
                  placeholder="Add personal notes about this stream..."
                  rows={2}
                  maxLength={200}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.notes.length}/200 characters
                </p>
              </div>

              {/* Channel Number */}
              {formData.category_id && (() => {
                const selectedCategory = categories.find(c => c.id === formData.category_id)
                const hasRange = selectedCategory?.channel_range_start !== undefined && selectedCategory?.channel_range_end !== undefined
                return (
                  <div>
                    <label className="form-label">
                      Channel Number {selectedCategory?.auto_assign_numbers ? '(auto-assigned if empty)' : '(optional)'}
                    </label>
                    <input
                      type="number"
                      value={formData.channel_number}
                      onChange={(e) => setFormData({ ...formData, channel_number: e.target.value })}
                      className="input-field"
                      placeholder={hasRange ? `${selectedCategory?.channel_range_start} - ${selectedCategory?.channel_range_end}` : 'Enter channel number'}
                      min={selectedCategory?.channel_range_start}
                      max={selectedCategory?.channel_range_end}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      {hasRange
                        ? `Allowed range: ${selectedCategory?.channel_range_start} - ${selectedCategory?.channel_range_end}`
                        : 'No channel range configured for this category'}
                      {selectedCategory?.auto_assign_numbers && ' (will auto-assign if left empty)'}
                    </p>
                  </div>
                )
              })()}

              <div>
                <label className="form-label">Startup Mode</label>
                <select
                  value={formData.startup_mode}
                  onChange={(e) => setFormData({ ...formData, startup_mode: e.target.value as 'on_demand' | 'always_on' })}
                  className="input-field"
                >
                  <option value="on_demand">On Demand</option>
                  <option value="always_on">Always On</option>
                </select>
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.startup_mode === 'always_on'
                    ? 'Stream starts on server boot and never stops'
                    : 'Stream starts when a viewer requests it'}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="stream_is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="stream_is_active" className="text-base text-foreground">Active</label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="stream_fix_timestamps"
                  checked={formData.fix_timestamps}
                  onChange={(e) => setFormData({ ...formData, fix_timestamps: e.target.checked })}
                  className="w-4 h-4"
                />
                <div>
                  <label htmlFor="stream_fix_timestamps" className="text-base text-foreground">Fix Timestamps</label>
                  <p className="text-sm text-muted-foreground">Enable for streams with playback freezing (HBO, Reelz, etc.)</p>
                </div>
              </div>
            </form>
            </div>

            {/* Sticky Footer */}
            <div className="flex gap-3 p-4 border-t border-border flex-shrink-0 bg-card">
              <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                Cancel
              </button>
              <button
                type="submit"
                form="stream-form"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn-primary flex-1"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingStream
                  ? 'Update'
                  : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Category Modal */}
      {bulkCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setBulkCategoryModalOpen(false)} />
          <div className="relative bg-card rounded-lg border border-border w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-4">
              Change Category for {selectedStreams.size} streams
            </h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Select Category</label>
                <select
                  id="bulk-category-select"
                  className="input-field"
                  defaultValue=""
                >
                  <option value="">Remove category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setBulkCategoryModalOpen(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const select = document.getElementById('bulk-category-select') as HTMLSelectElement
                    const categoryId = select.value
                    batchUpdateMutation.mutate({
                      streamIds: Array.from(selectedStreams),
                      updates: { category_id: categoryId || null }
                    })
                  }}
                  disabled={batchUpdateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {batchUpdateMutation.isPending ? 'Updating...' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk User-Agent Modal */}
      {bulkUserAgentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setBulkUserAgentModalOpen(false)} />
          <div className="relative bg-card rounded-lg border border-border w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-4">
              Set User-Agent for {selectedStreams.size} streams
            </h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Custom User-Agent</label>
                <input
                  type="text"
                  value={bulkUserAgent}
                  onChange={(e) => setBulkUserAgent(e.target.value)}
                  className="input-field"
                  placeholder="Leave empty to clear custom user-agent"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Leave empty to remove custom user-agent and use global default
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setBulkUserAgentModalOpen(false)
                    setBulkUserAgent('')
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    batchUpdateMutation.mutate({
                      streamIds: Array.from(selectedStreams),
                      updates: { custom_user_agent: bulkUserAgent }
                    })
                  }}
                  disabled={batchUpdateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {batchUpdateMutation.isPending ? 'Updating...' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Stream Modal */}
      {testModalOpen && testingStream && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeTestModal} />
          <div className="relative bg-card rounded-lg border border-border w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <Monitor className="w-5 h-5 text-primary shrink-0" />
                <h2 className="text-base sm:text-xl font-semibold text-foreground truncate">
                  Test: {testingStream.name}
                </h2>
                {testStreamData?.state && (
                  <span className={`badge-${testStreamData.state === 'running' ? 'success' : testStreamData.state === 'error' ? 'destructive' : 'secondary'}`}>
                    {testStreamData.state}
                  </span>
                )}
              </div>
              <button
                onClick={closeTestModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row min-h-0">
              {/* Video Player Section */}
              <div className="lg:w-1/2 p-4 flex flex-col">
                <div className="aspect-video bg-black rounded-lg flex items-center justify-center overflow-hidden relative">
                  {testLoading ? (
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Starting stream...</p>
                    </div>
                  ) : testStreamData?.error ? (
                    <div className="text-center p-4">
                      <p className="text-destructive font-medium mb-2">Stream Error</p>
                      <p className="text-sm text-muted-foreground">{testStreamData.error}</p>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        className="w-full h-full"
                        autoPlay
                        muted
                        controls
                        playsInline
                      />
                      {!hlsRef.current && testStreamData?.logs && testStreamData.logs.length > 0 && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                            <p className="text-muted-foreground text-sm">Buffering segments...</p>
                          </div>
                        </div>
                      )}
                      {!testStreamData?.logs?.length && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <p className="text-muted-foreground">Waiting for stream...</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Stream Info */}
                {testStreamData?.tech_info && (
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <h3 className="text-sm font-medium text-foreground mb-3">Stream Information</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {testStreamData.tech_info.resolution && (
                        <div>
                          <span className="text-muted-foreground">Resolution:</span>
                          <span className="ml-2 text-foreground font-medium">{testStreamData.tech_info.resolution}</span>
                        </div>
                      )}
                      {testStreamData.tech_info.video_codec && (
                        <div>
                          <span className="text-muted-foreground">Video:</span>
                          <span className="ml-2 text-foreground font-medium">{testStreamData.tech_info.video_codec}</span>
                        </div>
                      )}
                      {testStreamData.tech_info.audio_codec && (
                        <div>
                          <span className="text-muted-foreground">Audio:</span>
                          <span className="ml-2 text-foreground font-medium">{testStreamData.tech_info.audio_codec}</span>
                        </div>
                      )}
                      {testStreamData.tech_info.fps && (
                        <div>
                          <span className="text-muted-foreground">FPS:</span>
                          <span className="ml-2 text-foreground font-medium">{testStreamData.tech_info.fps}</span>
                        </div>
                      )}
                      {testStreamData.tech_info.bitrate && (
                        <div>
                          <span className="text-muted-foreground">Bitrate:</span>
                          <span className="ml-2 text-foreground font-medium">{testStreamData.tech_info.bitrate}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* FFmpeg Logs Section */}
              <div className="lg:w-1/2 border-t lg:border-t-0 lg:border-l border-border flex flex-col min-h-0">
                <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">FFmpeg Output</span>
                    <span className="text-xs text-muted-foreground">
                      ({testStreamData?.logs?.length || 0} lines)
                    </span>
                  </div>
                  <button
                    onClick={refreshLogs}
                    className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    title="Refresh logs"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <div
                  ref={logContainerRef}
                  className="flex-1 overflow-auto p-3 bg-zinc-950 font-mono text-xs min-h-[200px] max-h-[400px]"
                >
                  {testStreamData?.logs && testStreamData.logs.length > 0 ? (
                    testStreamData.logs.map((log, i) => (
                      <div
                        key={i}
                        className={`py-0.5 ${
                          log.toLowerCase().includes('error')
                            ? 'text-red-400'
                            : log.toLowerCase().includes('warning')
                            ? 'text-yellow-400'
                            : 'text-zinc-300'
                        }`}
                      >
                        {log}
                      </div>
                    ))
                  ) : (
                    <div className="text-zinc-500 italic">No logs yet...</div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
              <div className="text-sm text-muted-foreground">
                Source: <span className="text-foreground">{testingStream.sources?.[0]?.url || 'N/A'}</span>
              </div>
              <div className="flex gap-3">
                {testStreamData && !testStreamData.error && testStreamData.state !== 'stopped' && (
                  <button
                    onClick={async () => {
                      try {
                        await api.stopTestStream(testingStream.id)
                      } catch {}
                      setTestStreamData(prev => prev ? { ...prev, state: 'stopped' } : prev)
                      stopLogPolling()
                      if (hlsRef.current) {
                        hlsRef.current.destroy()
                        hlsRef.current = null
                      }
                    }}
                    className="btn-secondary"
                  >
                    <Square className="w-4 h-4" />
                    Stop Stream
                  </button>
                )}
                <button onClick={closeTestModal} className="btn-primary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
